import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';

interface Me {
  id: string;
  email: string;
  name: string;
  role: string;
  tenant_id: string | null;
  tenant_name: string | null;
  tenant_slug: string | null;
}

export function AppShell() {
  const nav = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ user: Me }>('/auth/me');
        setMe(res.user);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) nav('/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 grid place-items-center">
        <div className="w-8 h-8 border-2 border-ink-200 border-t-brand-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (!me) return null;

  async function logout() {
    try { await api.post('/auth/logout'); } catch {}
    nav('/login');
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[260px_1fr] bg-ink-50">
      <aside className="bg-white border-r border-ink-200 p-5 flex flex-col">
        <Link to="/app" className="inline-flex items-center gap-2 font-semibold tracking-tight text-ink-900 mb-1">
          <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
            <rect width="32" height="32" rx="7" fill="#0f172a" />
            <path d="M9 16l5 5 9-11" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          HOA-Vote
        </Link>
        <div className="text-xs text-ink-500 mb-7 truncate pl-7">{me.tenant_name ?? 'Operator console'}</div>

        <nav className="flex flex-col gap-0.5 text-sm">
          <NavItem to="/app" end icon={DashboardIcon}>Dashboard</NavItem>
          <NavItem to="/app/elections" icon={VoteIcon}>Elections</NavItem>
          <NavItem to="/app/residents" icon={UsersIcon}>Residents</NavItem>
          <NavItem to="/app/lots" icon={HomeIcon}>Lots</NavItem>
          <NavItem to="/app/meetings" icon={CalendarIcon}>Meetings</NavItem>
          <NavItem to="/app/documents" icon={FileIcon}>Documents</NavItem>
          <NavItem to="/app/audit" icon={ChainIcon}>Audit log</NavItem>
          <NavItem to="/app/settings" icon={SettingsIcon}>Settings</NavItem>
          {me.role === 'superadmin' && (
            <>
              <div className="mt-4 mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-ink-400">Operator</div>
              <NavItem to="/app/super" icon={ShieldIcon}>Tenants</NavItem>
            </>
          )}
        </nav>

        <div className="mt-auto pt-6 border-t border-ink-100">
          <div className="flex items-center gap-2.5 px-2">
            <div className="w-8 h-8 rounded-full bg-ink-100 text-ink-700 grid place-items-center text-xs font-semibold">
              {me.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-ink-900 truncate">{me.name}</div>
              <div className="text-xs text-ink-500 truncate">{me.email}</div>
            </div>
          </div>
          <button onClick={logout} className="mt-3 w-full text-left text-xs text-ink-500 hover:text-ink-900 px-2 py-1.5 rounded hover:bg-ink-50 transition">
            Sign out
          </button>
        </div>
      </aside>

      <main className="bg-ink-50 overflow-auto">
        <div className="max-w-6xl mx-auto p-6 sm:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function NavItem({ to, end, icon: Icon, children }: { to: string; end?: boolean; icon: () => React.ReactElement; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-lg transition ${
          isActive
            ? 'bg-ink-900 text-white'
            : 'text-ink-700 hover:bg-ink-100 hover:text-ink-900'
        }`
      }
    >
      <Icon />
      <span>{children}</span>
    </NavLink>
  );
}

const iconProps = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
function DashboardIcon() { return <svg {...iconProps}><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></svg>; }
function VoteIcon() { return <svg {...iconProps}><polyline points="20 6 9 17 4 12" /></svg>; }
function UsersIcon() { return <svg {...iconProps}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>; }
function HomeIcon() { return <svg {...iconProps}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>; }
function CalendarIcon() { return <svg {...iconProps}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>; }
function FileIcon() { return <svg {...iconProps}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>; }
function SettingsIcon() { return <svg {...iconProps}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>; }
function ShieldIcon() { return <svg {...iconProps}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>; }
function ChainIcon() { return <svg {...iconProps}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>; }
